import React, { useEffect, useState } from 'react';
import { X, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { sidepotAPI } from '../../utils/api';

/**
 * SidepotEditModal
 *
 * Unified create/edit dialog for sidepots. Mirrors the fields in ShowCreate
 * so producers can manage sidepot divisions, payouts, scheduled day, and
 * added money directly from the show page — no round-trip to the edit page.
 *
 * Props:
 *   - showId:       parent show id
 *   - classes:      show.classes (for rollover-source dropdown)
 *   - sidepot:      existing sidepot obj (undefined → create-new mode)
 *   - onClose():    dismiss modal
 *   - onSaved():    called with the updated/created sidepot; parent re-fetches the show
 */
export const SidepotEditModal = ({ showId, classes = [], sidepot, onClose, onSaved }) => {
  const isEdit = !!sidepot?.id;
  const [form, setForm] = useState({
    name: '',
    entry_fee: '',
    division_type: 'NONE',
    division_split: 0.5,
    payout_pct: 100,
    added_money: 0,
    scheduled_day: '',
    rollover_from_class_ids: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      setForm({
        name: sidepot.name || '',
        entry_fee: sidepot.entry_fee ?? '',
        division_type: sidepot.division_type || 'NONE',
        division_split: sidepot.division_split ?? 0.5,
        payout_pct: sidepot.payout_pct ?? 100,
        added_money: sidepot.added_money ?? 0,
        scheduled_day: sidepot.scheduled_day || '',
        rollover_from_class_ids: sidepot.rollover_from_class_ids || [],
      });
    }
  }, [sidepot, isEdit]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const toggleRollover = (cid) => {
    const cur = new Set(form.rollover_from_class_ids);
    if (cur.has(cid)) cur.delete(cid); else cur.add(cid);
    update('rollover_from_class_ids', Array.from(cur));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Sidepot name is required'); return; }
    if (form.entry_fee === '' || isNaN(parseFloat(form.entry_fee))) {
      toast.error('Entry fee is required'); return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      entry_fee: parseFloat(form.entry_fee),
      division_type: form.division_type,
      division_split: form.division_type === '2D' ? parseFloat(form.division_split) || 0.5 : null,
      payout_pct: parseFloat(form.payout_pct ?? 100),
      added_money: parseFloat(form.added_money ?? 0),
      scheduled_day: form.scheduled_day || '',
      rollover_from_class_ids: form.rollover_from_class_ids,
    };
    try {
      if (isEdit) {
        await sidepotAPI.update(showId, sidepot.id, payload);
        toast.success('Sidepot updated');
      } else {
        await sidepotAPI.create(showId, payload);
        toast.success('Sidepot added');
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save sidepot');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm(`Delete sidepot "${sidepot.name}"? This removes it from all rider entries and deletes any calculated payouts.`)) return;
    setSaving(true);
    try {
      await sidepotAPI.delete(showId, sidepot.id);
      toast.success('Sidepot deleted');
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete sidepot');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => !saving && onClose()} data-testid="sidepot-edit-modal">
      <div className="bg-[#161616] border border-[#262626] rounded-lg p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>
            {isEdit ? 'Edit Sidepot' : 'Add Sidepot'}
          </h3>
          <button onClick={onClose} className="text-[#9E9E9E] hover:text-white" data-testid="sidepot-modal-close"><X size={18} /></button>
        </div>

        <div className="space-y-3">
          {/* Name + Entry Fee */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#F8F8F8] mb-1">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder="e.g., Youth Sidepot"
                className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FACC15]"
                data-testid="sidepot-modal-name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#F8F8F8] mb-1">Entry Fee ($)</label>
              <input
                type="number"
                step="1"
                min="0"
                value={form.entry_fee}
                onChange={(e) => update('entry_fee', e.target.value)}
                placeholder="10"
                className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FACC15]"
                data-testid="sidepot-modal-entry-fee"
              />
            </div>
          </div>

          {/* Division Type + 2D Split */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#F8F8F8] mb-1">Division Type</label>
              <select
                value={form.division_type}
                onChange={(e) => update('division_type', e.target.value)}
                className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FACC15]"
                data-testid="sidepot-modal-division-type"
              >
                <option value="NONE">None (Overall)</option>
                <option value="2D">2D</option>
                <option value="3D">3D</option>
                <option value="4D">4D</option>
              </select>
            </div>
            {form.division_type === '2D' && (
              <div>
                <label className="block text-xs font-medium text-[#F8F8F8] mb-1">2D Split (sec)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.division_split}
                  onChange={(e) => update('division_split', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FACC15]"
                  data-testid="sidepot-modal-2d-split"
                />
              </div>
            )}
          </div>

          {/* Payout % + Added Money */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#F8F8F8] mb-1">Payout %</label>
              <input
                type="number"
                step="1"
                min="0"
                max="100"
                value={form.payout_pct}
                onChange={(e) => update('payout_pct', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FACC15]"
                data-testid="sidepot-modal-payout-pct"
              />
              <p className="text-[10px] text-[#555] mt-0.5">% of pool distributed to places</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#F8F8F8] mb-1">Added Money ($)</label>
              <input
                type="number"
                step="1"
                min="0"
                value={form.added_money}
                onChange={(e) => update('added_money', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FACC15]"
                data-testid="sidepot-modal-added-money"
              />
            </div>
          </div>

          {/* Scheduled Day */}
          <div>
            <label className="block text-xs font-medium text-[#F8F8F8] mb-1">Scheduled Day</label>
            <select
              value={form.scheduled_day}
              onChange={(e) => update('scheduled_day', e.target.value)}
              className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#FACC15]"
              data-testid="sidepot-modal-scheduled-day"
            >
              <option value="">Any day</option>
              {['Friday', 'Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'].map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          {/* Linked Classes */}
          {classes.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-[#F8F8F8] mb-1">
                Linked Classes <span className="text-[#555] font-normal">(riders in these classes can opt in)</span>
              </label>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 bg-[#0A0A0A] border border-[#262626] rounded-md">
                {classes.map(c => {
                  const active = form.rollover_from_class_ids.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleRollover(c.id)}
                      className={`px-2 py-1 text-xs rounded border transition-colors ${
                        active
                          ? 'bg-[#FACC15]/20 border-[#FACC15] text-[#FACC15]'
                          : 'bg-[#161616] border-[#262626] text-[#9E9E9E] hover:border-[#FACC15]/50'
                      }`}
                      data-testid={`sidepot-modal-class-toggle-${c.id}`}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-5 pt-4 border-t border-[#262626]">
          {isEdit && (
            <button
              onClick={handleDelete}
              disabled={saving}
              className="flex items-center gap-1 px-3 py-2 bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] rounded-md text-xs font-semibold hover:bg-[#FF3B30]/20 disabled:opacity-50"
              data-testid="sidepot-modal-delete"
            >
              <Trash size={14} weight="bold" /> Delete
            </button>
          )}
          <div className="ml-auto flex gap-2">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] rounded-md text-xs font-semibold hover:text-white disabled:opacity-50"
              data-testid="sidepot-modal-cancel"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-[#FACC15] hover:bg-[#FACC15]/80 text-black rounded-md text-xs font-bold disabled:opacity-50"
              data-testid="sidepot-modal-save"
            >
              {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Sidepot')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SidepotEditModal;
